/**
 * Página de gestión de ingredientes nutricionales
 * Permite gestionar ingredientes base con información nutricional del proveedor
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Card,
  Space,
  Button,
  Typography,
  Tag,
  message,
  Statistic,
  Row,
  Col,
  Popconfirm,
} from "antd";
import {
  ReloadOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useBranch } from "../context/BranchContext";
import api from "../api/endpoints";
import NutritionalIngredientModal from "../components/nutritional/IngredientForm";

const { Title, Paragraph } = Typography;

const NutritionalIngredients = () => {
  const { activeBranch } = useBranch();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    withNutrition: 0,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState(null);

  const loadIngredients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.recipes.getNutritionalIngredients(activeBranch);
      const ingredientsList = Array.isArray(response) ? response : [];
      setIngredients(ingredientsList);
      setStats({
        total: ingredientsList.length,
        withNutrition: ingredientsList.filter((i) => i.energy_kcal).length,
      });
    } catch (error) {
      console.error("Error loading ingredients:", error);
      message.error("Error al cargar ingredientes: " + (error.message || "Error desconocido"));
      setIngredients([]);
      setStats({ total: 0, withNutrition: 0 });
    } finally {
      setLoading(false);
    }
  }, [activeBranch]);

  useEffect(() => {
    loadIngredients();
  }, [loadIngredients]);

  const handleCreate = () => {
    setSelectedIngredient(null);
    setModalVisible(true);
  };

  const handleEdit = (ingredient) => {
    setSelectedIngredient(ingredient);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.products.destroy(id);
      message.success("Ingrediente eliminado exitosamente");
      loadIngredients();
    } catch (error) {
      message.error("Error al eliminar ingrediente");
    }
  };

  const handleModalSuccess = () => {
    setModalVisible(false);
    setSelectedIngredient(null);
    // Pequeño delay para asegurar que localStorage se actualice
    setTimeout(() => {
      loadIngredients();
    }, 100);
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Proveedor",
      dataIndex: "supplier_company",
      key: "supplier_company",
      render: (text) => text || <Tag color="default">Sin proveedor</Tag>,
    },
    {
      title: "Unidad",
      dataIndex: "measurement_unit",
      key: "measurement_unit",
      width: 100,
    },
    {
      title: "Energía (kcal)",
      dataIndex: "energy_kcal",
      key: "energy_kcal",
      width: 120,
      render: (val) => (val ? val.toFixed(2) : "-"),
      align: "right",
    },
    {
      title: "Proteínas (g)",
      dataIndex: "proteins_g",
      key: "proteins_g",
      width: 120,
      render: (val) => (val ? val.toFixed(2) : "-"),
      align: "right",
    },
    {
      title: "Grasas (g)",
      dataIndex: "total_fats_g",
      key: "total_fats_g",
      width: 120,
      render: (val) => (val ? val.toFixed(2) : "-"),
      align: "right",
    },
    {
      title: "Carbohidratos (g)",
      dataIndex: "carbohydrates_g",
      key: "carbohydrates_g",
      width: 140,
      render: (val) => (val ? val.toFixed(2) : "-"),
      align: "right",
    },
    {
      title: "Estado",
      key: "status",
      width: 120,
      render: (_, record) => (
        <Tag color={record.energy_kcal ? "green" : "orange"}>
          {record.energy_kcal ? (
            <>
              <CheckCircleOutlined /> Completo
            </>
          ) : (
            "Incompleto"
          )}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar este ingrediente?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              Ingredientes para tus Recetas
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 24 }}>
              Aquí registras todos los ingredientes que usas para hacer tus helados (leche, azúcar, crema, etc.) 
              con su información nutricional. Luego podrás usar estos ingredientes cuando crees tus recetas.
            </Paragraph>
          </div>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12}>
              <Statistic title="Total Ingredientes" value={stats.total} />
            </Col>
            <Col xs={24} sm={12}>
              <Statistic
                title="Con Datos Nutricionales"
                value={stats.withNutrition}
                valueStyle={{ color: "#3f8600" }}
              />
            </Col>
          </Row>

          <Space wrap style={{ width: "100%" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              className="mobile-full-width"
            >
              Agregar Nuevo Ingrediente
            </Button>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadIngredients}
              className="mobile-full-width"
            >
              Actualizar
            </Button>
          </Space>

          <Table
            columns={columns}
            dataSource={Array.isArray(ingredients) ? ingredients : []}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} ingredientes`,
              pageSizeOptions: ["10", "20", "50", "100"],
              responsive: true,
            }}
            scroll={{ x: 1200 }}
            size="middle"
            bordered={false}
            responsive
          />
        </Space>

        <NutritionalIngredientModal
          visible={modalVisible}
          ingredient={selectedIngredient}
          onCancel={() => {
            setModalVisible(false);
            setSelectedIngredient(null);
          }}
          onSuccess={handleModalSuccess}
        />
      </Card>
    </div>
  );
};

export default NutritionalIngredients;

